import {
  useReactTable,
  getCoreRowModel,
  ColumnDef,
  flexRender,
  VisibilityState,
  ColumnSizingState,
  ColumnSort,
} from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Dropdown,
  OverlayTrigger,
  Row,
  Spinner,
  Table,
  Tooltip,
} from "react-bootstrap";
import styles from "./bookingPage.module.scss";
import BookingSettings from "../booking-settings/BookingSettings";
import BookingMeta from "../booking-meta/BookingMeta";
import { useDispatch, useSelector } from "react-redux";
import bookingPageSlice, { Booking } from "./store/slice";
import bookingSettingSlice from "../booking-settings/store/slice";
import {
  BsSortUp,
  BsSortDown,
  BsThreeDots,
  BsClipboard,
  BsEye,
  BsMailbox,
  BsPrinter,
  BsXCircle,
  BsPencil,
  BsQuestionCircle,
  BsArchive,
} from "react-icons/bs";
import CopyToClipboard from "react-copy-to-clipboard";
import { showSuccess } from "../toaster/store/slice";
import { formatDate, formatUTCDate, formatUTCTime } from "../../utils/date";
import { Route, Routes, useNavigate } from "react-router-dom";
import BookingForm from "../booking-form/BookingForm";
import BookingShow from "../booking-show/BookingShow";
import ConfirmModal from "../confirm-modal/ConfirmModal";
import { getBookingTextData } from "../../utils/text-data";
import handlePrint from "../../utils/print";
import BookingPricing from "../booking-pricing/BookingPricing";
import StatusBadge from "../status-badge/StatusBadge";
import { hasRole } from "../../utils/roles";
import { RoleCode } from "../users/store/slice";
import {
  bookingIsCancelable,
  bookingIsEditable,
} from "../../utils/permissions";
import LiveQueueBadge from "../live-queue-badge/LiveQueueBadge";

const defaultColumns: ColumnDef<Booking>[] = [
  {
    id: "id",
    header: () => "Booking ID",
    cell: ({
      row: {
        original: { id },
      },
    }) => id,
  },
  {
    id: "booking_status_id",
    header: () => "Booking status",
    cell: ({
      row: {
        original: { id, booking_status, archived_at },
      },
    }) => {
      return (
        <div className={styles.bookingStatusColumn}>
          {!!archived_at && (
            <OverlayTrigger
              placement="top"
              overlay={(props) => (
                <Tooltip id={`booking-archive-tooltip-${id}`} {...props}>
                  Booking is archived
                </Tooltip>
              )}
            >
              <span
                className="text-secondary"
                style={{ marginRight: "0.5rem" }}
              >
                <BsArchive />
              </span>
            </OverlayTrigger>
          )}

          <StatusBadge status={booking_status} />
        </div>
      );
    },
  },
  {
    id: "ticket_status_id",
    header: () => "Ticket status",
    cell: ({
      row: {
        original: { ticket_status },
      },
    }) => <StatusBadge status={ticket_status} />,
  },
  {
    id: "invoice_status_id",
    header: () => "Invoice status",
    cell: ({
      row: {
        original: { invoice_status, documents },
      },
    }) => (
      <StatusBadge
        status={invoice_status}
        showDocumentIcon={documents.length > 0}
      />
    ),
  },
  {
    accessorKey: "booking_reference",
    header: () => "Booking reference",
    cell: (info) => info.getValue() ?? "-",
  },
  {
    accessorKey: "ordered_at",
    header: () => "Request date",
    cell: (info) => formatDate(info.getValue() as string),
  },
  {
    id: "departure_date",
    accessorFn: (row) => row.departure_at,
    header: () => "Departure date",
    cell: (info) => {
      return formatUTCDate(info.getValue() as string);
    },
    enableSorting: false,
  },
  {
    id: "departure_time",
    accessorFn: (row) => row.departure_at,
    header: () => "Departure time",
    cell: (info) => {
      const { route } = info.row.original;

      return (
        <div>
          {formatUTCTime(info.getValue() as string)}{" "}
          {route.is_transit && (
            <LiveQueueBadge validityDays={route.validity_days} />
          )}
        </div>
      );
    },
    enableSorting: false,
  },
  {
    id: "arrival_date",
    accessorFn: (row) => row.arrival_at,
    header: () => "Arrival date",
    cell: (info) => {
      const isTransit = info.row.original.route.is_transit;

      if (isTransit) return "-";

      return formatUTCDate(info.getValue() as string);
    },
    enableSorting: false,
  },
  {
    id: "arrival_time",
    accessorFn: (row) => row.arrival_at,
    header: () => "Arrival time",
    cell: (info) => {
      const isTransit = info.row.original.route.is_transit;

      if (isTransit) return "-";

      return formatUTCTime(info.getValue() as string);
    },
    enableSorting: false,
  },
  {
    id: "departure_country",
    accessorFn: (row) => row.route.departure_country.name,
    header: () => "Departure country",
    cell: (info) => info.getValue(),
    enableSorting: false,
  },
  {
    id: "arrival_country",
    accessorFn: (row) => row.route.arrival_country.name,
    header: () => "Arrival country",
    cell: (info) => info.getValue(),
    enableSorting: false,
  },
  {
    id: "route_code",
    accessorFn: (row) => row.route.description,
    header: () => "Route",
    cell: (info) => <Badge bg="dark">{info.getValue() as string}</Badge>,
    enableSorting: false,
  },
  {
    accessorKey: "customer_reference",
    header: () => "Customer reference",
    cell: (info) => info.getValue() ?? "-",
  },
  {
    accessorKey: "vehicle_number",
    header: () => "Vehicle no.",
    cell: (info) => info.getValue() ?? "-",
  },
  {
    accessorKey: "trailer_number",
    header: () => "Trailer no.",
    cell: (info) => info.getValue() ?? "-",
  },
  {
    id: "vehicle_type_id",
    accessorFn: (row) => row.vehicle_type.name,
    header: () => "Vehicle type",
    cell: (info) => info.getValue(),
  },
  {
    id: "trailer_type_id",
    accessorFn: (row) => row.trailer_type?.name,
    header: () => "Trailer type",
    cell: (info) => info.getValue() ?? "-",
  },
  {
    id: "cargo_type_id",
    accessorFn: (row) => row.cargo_type?.name,
    header: () => "Cargo type",
    cell: (info) => info.getValue() ?? "-",
  },
  {
    accessorKey: "plug_in",
    header: () => "Plug-in",
    cell: (info) => (info.getValue() ? "Yes" : "No"),
  },
  {
    accessorKey: "adr",
    header: () => "ADR",
    cell: (info) => (info.getValue() ? "Yes" : "No"),
  },
  {
    id: "created_by",
    header: () => "Created by",
    cell: ({
      row: {
        original: { creator },
      },
    }) => creator.name,
    enableSorting: false,
  },
];

const BookingPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [cancellationId, setCancellationId] = useState<number>();

  const profile = useSelector((state) => state.loginPage.profile);
  const data = useSelector((state) => state.bookingPage.data);
  const meta = useSelector((state) => state.bookingPage.meta);
  const isLoading = useSelector((state) => state.bookingPage.isLoading);
  const filtersReady = useSelector((state) => state.bookingFilters.isReady);
  const settingsReady = useSelector((state) => state.bookingSettings.isReady);
  const columnVisibility = useSelector(
    (state) => state.bookingSettings.columnVisibility
  );
  const columnSizing = useSelector(
    (state) => state.bookingSettings.columnSizing
  );
  const sorting = useSelector((state) => state.bookingSettings.sorting);
  const tableSize = useSelector((state) => state.bookingSettings.tableSize);
  const columnOrder = useSelector((state) => state.bookingSettings.columnOrder);

  const columns = useMemo(() => defaultColumns, []);

  const fetchData = useCallback(
    () => dispatch(bookingPageSlice.actions.fetchData()),
    [dispatch]
  );
  const loadMore = useCallback(
    () => dispatch(bookingPageSlice.actions.loadMore()),
    [dispatch]
  );
  const setColumnVisibility = useCallback(
    (state: VisibilityState) =>
      dispatch(bookingSettingSlice.actions.setColumnVisibility(state)),
    [dispatch]
  );
  const setColumnSizing = useCallback(
    (state: ColumnSizingState) =>
      dispatch(bookingSettingSlice.actions.setColumnSizing(state)),
    [dispatch]
  );
  const setSorting = useCallback(
    (state: ColumnSort[]) =>
      dispatch(bookingSettingSlice.actions.setSorting(state)),
    [dispatch]
  );
  const reset = useCallback(
    () => dispatch(bookingPageSlice.actions.reset()),
    [dispatch]
  );
  const showCopied = useCallback(
    () => dispatch(showSuccess("Copied to clipboard.")),
    [dispatch]
  );
  const cancel = useCallback(
    (id: number) => dispatch(bookingPageSlice.actions.cancel(id)),
    [dispatch]
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      columnVisibility,
      columnSizing,
      columnOrder,
      sorting,
    },
    onColumnVisibilityChange: (state: any) => setColumnVisibility(state()),
    onColumnSizingChange: (state: any) => {
      setTimeout(() => {
        setColumnSizing(state());
      }, 1);
    },
    onSortingChange: (state: any) => {
      setSorting(state());
    },
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    fetchData();

    return () => {
      reset();
    };
  }, [fetchData, reset]);

  if (!profile) return null;

  const isInternal = hasRole(profile, RoleCode.Internal);

  return (
    <>
      <Container fluid>
        <Row>
          <Col>
            <BookingMeta data={data} meta={meta} />
          </Col>
        </Row>

        <Row>
          <Col>
            <BookingSettings table={table} />
          </Col>
        </Row>
        <Row>
          <Col>
            <Card className={styles.card}>
              <Card.Body>
                <Table
                  size={tableSize}
                  id="bookings-table"
                  responsive
                  className={styles.table}
                  borderless
                  striped
                  hover
                  {...{
                    style: {
                      width: table.getCenterTotalSize(),
                    },
                  }}
                >
                  <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th
                            {...{
                              key: header.id,
                              colSpan: header.colSpan,
                              style: {
                                width: header.getSize(),
                              },
                            }}
                          >
                            {header.isPlaceholder ? null : (
                              <div
                                {...{
                                  className: `${
                                    header.column.getCanSort()
                                      ? styles.cursorPointer
                                      : styles.cursorInitial
                                  } ${styles.heading}`,
                                  onClick:
                                    header.column.getToggleSortingHandler(),
                                }}
                              >
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                                {{
                                  asc: <BsSortUp />,
                                  desc: <BsSortDown />,
                                }[header.column.getIsSorted() as string] ??
                                  null}
                              </div>
                            )}
                            <div
                              {...{
                                onMouseDown: header.getResizeHandler(),
                                onTouchStart: header.getResizeHandler(),
                                className: `${styles.resizer} ${
                                  header.column.getIsResizing()
                                    ? styles.isResizing
                                    : ""
                                }`,
                                style: {
                                  transform: header.column.getIsResizing()
                                    ? `translateX(${
                                        table.getState().columnSizingInfo
                                          .deltaOffset
                                      }px)`
                                    : "",
                                },
                              }}
                            />
                          </th>
                        ))}
                        <th />
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {!!(
                      !settingsReady ||
                      !filtersReady ||
                      (isLoading && data.length === 0)
                    ) && (
                      <tr className={styles.loading}>
                        <td colSpan={22}>
                          <Spinner
                            className={`text-primary ${styles.spinner}`}
                            animation="border"
                          />
                        </td>
                      </tr>
                    )}
                    {!!(
                      settingsReady &&
                      filtersReady &&
                      !isLoading &&
                      data.length === 0
                    ) && (
                      <tr className={styles.noData}>
                        <td colSpan={22}>
                          <span className="text-muted">
                            No bookings to show.
                          </span>
                        </td>
                      </tr>
                    )}
                    {table.getRowModel().rows.map((row) => {
                      const data = row.original;
                      const isEditable = bookingIsEditable(data, profile);
                      const isCancelable = bookingIsCancelable(data, profile);
                      const bookingTextData = getBookingTextData(data);

                      return (
                        <tr key={row.id}>
                          {row.getVisibleCells().map((cell) => (
                            <td
                              {...{
                                key: cell.id,
                                style: {
                                  width: cell.column.getSize(),
                                },
                              }}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          ))}
                          <td>
                            <Dropdown drop={"start"}>
                              <Dropdown.Toggle
                                variant="outline-primary"
                                id="dropdown-basic"
                                size="sm"
                                className={styles.rowToggle}
                              >
                                <BsThreeDots />
                              </Dropdown.Toggle>

                              <Dropdown.Menu
                                className={styles.rowMenu}
                                popperConfig={{ strategy: "fixed" }}
                                renderOnMount
                              >
                                <Dropdown.Item
                                  onClick={() => navigate(`${data.id}`)}
                                >
                                  <BsEye /> Show
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => navigate(`${data.id}/edit`)}
                                  disabled={!isEditable}
                                >
                                  <BsPencil /> Edit
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => {
                                    navigate(`${data.id}/pricing`);
                                  }}
                                  disabled={!isEditable || isInternal}
                                >
                                  <BsQuestionCircle /> Ask price
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <CopyToClipboard
                                  text={bookingTextData}
                                  onCopy={showCopied}
                                >
                                  <Dropdown.Item>
                                    <BsClipboard /> Copy to clipboard
                                  </Dropdown.Item>
                                </CopyToClipboard>
                                <Dropdown.Item
                                  href={`mailto:?subject=Booking ${
                                    data.id
                                  }&body=${encodeURIComponent(
                                    bookingTextData
                                  )}`}
                                >
                                  <BsMailbox /> Send as mail
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => handlePrint(bookingTextData)}
                                >
                                  <BsPrinter /> Print
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item
                                  onClick={() => setCancellationId(data.id)}
                                  className={styles.cancelBooking}
                                  disabled={!isCancelable}
                                >
                                  <BsXCircle /> Cancel booking
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>

                {meta && (
                  <div className={styles.loadMore}>
                    {meta.current_page < meta.last_page ? (
                      <Button onClick={() => loadMore()}>
                        Load more
                        {isLoading && (
                          <Spinner
                            size="sm"
                            animation="border"
                            className={styles.loadMoreSpinner}
                          />
                        )}
                      </Button>
                    ) : data.length > 20 ? (
                      <span className="text-muted">Nothing more to show.</span>
                    ) : null}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <ConfirmModal
        question="Cancel booking?"
        submitText="Yes"
        isVisible={!!cancellationId}
        onHide={() => {
          setCancellationId(undefined);
        }}
        onSubmit={() => {
          cancel(Number(cancellationId));
          setCancellationId(undefined);
        }}
      />

      <Routes>
        <Route
          path="new"
          element={<BookingForm handleClose={() => navigate("/bookings")} />}
        />

        <Route
          path=":id/edit"
          element={<BookingForm handleClose={() => navigate("/bookings")} />}
        />

        <Route
          path=":id/pricing"
          element={<BookingPricing handleClose={() => navigate("/bookings")} />}
        />

        <Route
          path=":id"
          element={<BookingShow handleClose={() => navigate("/bookings")} />}
        />
      </Routes>
    </>
  );
};

export default BookingPage;
